import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TooltipModule } from "primeng/tooltip";
import { AngularSvgIconModule } from "angular-svg-icon";
import { CalibrationHrComment, CalibrationHrCommentVersion } from "alloy-performance-engine-cl";
import { TranslatePipe } from "../../pipe/translation.filter.pipe";
import { TRANSLATION_KEYS } from "../../constants/translations/translation-keys";
import { ApeAvatarComponent } from "../ape-avatar/ape-avatar.component";
import { ApeButtonComponent } from "../ape-button/ape-button.component";
import { MonitoringCalibrationBackofficeApiService } from "../../services/api/monitoring-calibration-backoffice-api.service";
import { AlertService } from "../../services/alert.service";
import { TranslateService } from "../../util/translate.service";
import { LoggerService } from "../../../ancestors/logger.service";

/**
 * Il Commento di HR di una scheda (spec Monitoraggio §9.5)
 *
 * E' il contenuto comune ai due punti da cui si accede al commento: il drawer aperto dalla voce di
 * menu della lista di monitoraggio e la sezione in fondo al drawer di dettaglio persona della
 * matrice. Carica da solo il commento della persona e lo salva: chi lo ospita non deve sapere
 * nulla della regola di scrittura, che arriva dal backend (`canEdit`).
 *
 * - Con il commento modificabile: campo di testo con Annulla e Salva, abilitati solo con una
 *   modifica, e un tooltip che lo spiega quando sono disabilitati. Salvare il campo vuoto rimuove
 *   il commento.
 * - Con il commento in sola lettura (scheda restituita, processo chiuso, valutazione riaperta): il
 *   solo testo.
 * - In entrambi i casi l'amministratore vede autore e storico delle modifiche, in una sezione
 *   ripiegabile chiusa all'apertura: si apre con un clic sul titolo.
 *
 * Il testo non salvato si salva da solo alla chiusura del drawer: lo chiede chi ospita il
 * componente, con `flushPendingChanges`.
 */
@Component({
  selector: "ape-calibration-hr-comment",
  standalone: true,
  imports: [
    CommonModule, FormsModule, TooltipModule, AngularSvgIconModule, TranslatePipe, ApeAvatarComponent, ApeButtonComponent
  ],
  templateUrl: "./ape-calibration-hr-comment.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApeCalibrationHrCommentComponent implements OnChanges {
  protected readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  /** Il processo */
  @Input({ required: true }) processId!: string;

  /** Il collaboratore valutato */
  @Input({ required: true }) userId!: string;

  /** Il commento dopo ogni salvataggio andato a buon fine */
  @Output() saved = new EventEmitter<CalibrationHrComment>();

  /** Il commento come l'ha restituito il backend, `null` finche' non e' caricato */
  protected comment: CalibrationHrComment | null = null;

  /** Il testo nel campo */
  protected draft: string = "";

  protected loading: boolean = false;

  protected saving: boolean = false;

  /** Un id stabile per legare etichetta e campo */
  protected readonly fieldId = `hr-comment-${Math.random()
    .toString(36)
    .slice(2, 11)}`;

  /** Se lo storico delle modifiche e' aperto: parte chiuso e si richiude cambiando persona */
  protected isHistoryExpanded: boolean = false;

  private calibrationApiService = inject(MonitoringCalibrationBackofficeApiService);

  private alertService = inject(AlertService);

  private translateService = inject(TranslateService);

  private logger = inject(LoggerService);

  private cdr = inject(ChangeDetectorRef);

  constructor() {
    this.logger.setCaller("ApeCalibrationHrCommentComponent");
  }

  /** Il testo salvato, stringa vuota se il commento non c'e' */
  protected get savedText(): string {
    return this.comment?.text ?? "";
  }

  /** Se il commento si puo' inserire o modificare */
  protected get canEdit(): boolean {
    return !!this.comment?.canEdit;
  }

  /**
   * Se nel campo c'e' una modifica non salvata
   *
   * Il confronto e' sul testo senza spazi in testa e in coda, come per gli altri campi di testo
   * dell'applicazione (`ape-text-area`): aggiungere uno spazio non e' una modifica.
   */
  get hasChanges(): boolean {
    return this.canEdit && this.draft.trim() !== this.savedText.trim();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.processId || changes.userId) && this.processId && this.userId) {
      this.isHistoryExpanded = false;
      void this.load();
    }
  }

  /**
   * Salva il testo non ancora salvato, se c'e'
   *
   * La spec vuole che chiudendo il drawer il testo non salvato venga salvato: lo chiama chi ospita
   * il componente prima di chiuderlo.
   */
  async flushPendingChanges(): Promise<void> {
    if (this.hasChanges && !this.saving) {
      await this.onSave();
    }
  }

  protected onDraftChange(value: string): void {
    this.draft = value ?? "";
  }

  /** Annulla: il campo torna al testo salvato */
  protected onCancel(): void {
    if (!this.hasChanges) {
      return;
    }
    this.draft = this.savedText;
  }

  /** Salva il commento; con il campo vuoto lo rimuove */
  protected async onSave(): Promise<void> {
    if (!this.hasChanges || this.saving) {
      return;
    }
    this.saving = true;
    this.cdr.markForCheck();
    try {
      this.comment = await this.calibrationApiService.saveHrComment(this.processId, this.userId, this.draft.trim());
      this.draft = this.savedText;
      this.saved.emit(this.comment);
    } catch (error) {
      this.logger.error("Error saving HR comment:", String(error));
      this.alertService.showError(this.translateService.instant<string>(TRANSLATION_KEYS.admin.calibrationTalentMatrix.hrComment.SAVE_ERROR));
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  /** Apre o chiude lo storico delle modifiche */
  protected toggleHistory(): void {
    this.isHistoryExpanded = !this.isHistoryExpanded;
  }

  /**
   * Il nome dell'autore di una versione
   * @param version La versione
   * @returns Cognome e nome, o l'id se l'anagrafica non e' risolta
   */
  protected authorDisplayName(version: CalibrationHrCommentVersion): string {
    const fullName = [version.authorSurname, version.authorForename]
      .filter(part => !!part)
      .join(" ");
    return fullName || version.authorUserId;
  }

  /**
   * Traccia le versioni per autore e data
   * @param _index La posizione, non usata
   * @param version La versione
   * @returns Una chiave stabile
   */
  protected trackByVersion(_index: number, version: CalibrationHrCommentVersion): string {
    return `${version.authorUserId}-${String(version.modifyDate)}`;
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.cdr.markForCheck();
    try {
      this.comment = await this.calibrationApiService.getHrComment(this.processId, this.userId);
      this.draft = this.savedText;
    } catch (error) {
      this.comment = null;
      this.draft = "";
      this.logger.error("Error loading HR comment:", String(error));
      this.alertService.showError(this.translateService.instant<string>(TRANSLATION_KEYS.admin.calibrationTalentMatrix.hrComment.LOAD_ERROR));
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }
}
